import { Archive, Pencil } from 'lucide-react';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { LocalTime } from '~/components/common/LocalTime';
import { ListToolbar } from '~/components/layout';
import {
  Card,
  CardBody,
  Chip,
  ConfirmModal,
  DataTable,
  Pagination,
  RowActionsMenu,
  StatusChip,
  type DataTableColumn,
} from '~/components/ui';
import { useListUrlState } from '~/hooks/useListUrlState';
import { matchesUpdateSearch } from '~/lib/flagFilters';
import { clampPage, pageCount } from '~/lib/listParams';
import { useArchiveUpdateMutation, useListUpdatesQuery } from '~/modules/query/featureFlags';

import {
  BADGE_LABELS,
  BADGE_TONES,
  FLAG_STATUS_LABELS,
  FLAG_STATUS_TONES,
  flagStatus,
} from '~/components/Constants/featureFlagsConstants';
import { errorText, orDash } from '~/components/pages/feature-flags/common';
import type { Reference } from '~/components/pages/feature-flags/common';
import type { ProductUpdate } from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

export const UpdatesTab = ({ reference }: { reference: Reference }) => {
  const list = useListUrlState();
  const updates = useListUpdatesQuery();
  const [archiveUpdate] = useArchiveUpdateMutation();
  const [archiving, setArchiving] = useState<ProductUpdate | null>(null);

  const rows = useMemo(
    () => (updates.data ?? []).filter((u) => matchesUpdateSearch(u, list.q)),
    [updates.data, list.q],
  );
  const pages = pageCount(rows.length, list.pageSize);
  const page = clampPage(list.page, rows.length, list.pageSize);
  const pageRows = rows.slice((page - 1) * list.pageSize, page * list.pageSize);

  const archive = (update: ProductUpdate) => {
    archiveUpdate(update.id)
      .unwrap()
      .then(() => toast.success('Product update archived'))
      .catch((err) =>
        toast.error(errorText(err, 'The product update could not be archived. Try again.')),
      );
  };

  const columns: DataTableColumn<ProductUpdate>[] = [
    {
      id: 'update',
      header: 'Update',
      cell: (u) => (
        <div className="max-w-md">
          <p className="font-medium text-gray-800 dark:text-white/90">{u.title}</p>
          {u.description && (
            <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={u.description}>
              {u.description}
            </p>
          )}
        </div>
      ),
    },
    { id: 'products', header: 'Products', cell: (u) => orDash(u.products.join(', ')) },
    {
      id: 'badge',
      header: 'Badge',
      cell: (u) =>
        u.badge ? (
          <Chip label={BADGE_LABELS[u.badge]} tone={BADGE_TONES[u.badge]} dot />
        ) : (
          orDash(null)
        ),
    },
    {
      id: 'toggle',
      header: 'Toggle',
      cell: (u) => (
        <div className="space-y-0.5">
          <p>{u.hasToggle ? 'User toggle with its own rules' : 'Announcement only'}</p>
          <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{u.key}</p>
        </div>
      ),
    },
    ...reference.environments.map<DataTableColumn<ProductUpdate>>((env) => ({
      id: `env-${env.key}`,
      header: env.name,
      cell: (u) => {
        const c = u.envs[env.key];
        const status = c ? flagStatus(c) : 'disabled';
        return (
          <StatusChip label={FLAG_STATUS_LABELS[status]} tone={FLAG_STATUS_TONES[status]} dot />
        );
      },
    })),
    { id: 'created', header: 'Created', cell: (u) => <LocalTime value={u.createdAt} /> },
    {
      id: 'actions',
      header: 'Actions',
      cell: (u) => (
        <RowActionsMenu
          testId={`feature-flags-updates-actions-${u.id}`}
          items={[
            {
              label: 'Edit',
              testId: 'edit',
              icon: <Pencil className="size-4" aria-hidden="true" />,
              href: routes.featureFlagsRecord('updates', u.id),
            },
            {
              label: 'Archive',
              testId: 'archive',
              icon: <Archive className="size-4" aria-hidden="true" />,
              onClick: () => setArchiving(u),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <Card>
        <ListToolbar
          title="Product updates"
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search product updates…',
            testId: 'feature-flags-updates-search',
          }}
        />
        <CardBody className="p-0">
          <DataTable<ProductUpdate>
            framed={false}
            testId="feature-flags-updates-table"
            loading={!updates.data && !updates.error}
            refreshing={updates.isFetching && !!updates.data}
            error={
              updates.error && !updates.data
                ? {
                    what: 'the product updates',
                    message: apiErrorMessage(updates.error as ApiErrorInput),
                    onRetry: updates.refetch,
                  }
                : undefined
            }
            emptyMessage={list.q ? 'No product updates match.' : 'No product updates yet.'}
            emptyDescription={
              list.q
                ? 'Clear the search or try another term.'
                : 'Write an update to tell customers what is new, what is coming and what they can try.'
            }
            data={pageRows}
            rowKey={(u) => u.id}
            href={(u) => routes.featureFlagsRecord('updates', u.id)}
            rowLabel={(u) => `Open ${u.title}`}
            columns={columns}
          />
        </CardBody>
        <Pagination
          pageIndex={page - 1}
          pageSize={list.pageSize}
          pageCount={pages}
          totalRows={rows.length}
          onPageChange={(index) => list.setPage(index + 1)}
          pageSizeControl={{
            value: list.pageSize,
            onChange: list.setPageSize,
            options: [...list.pageSizes],
          }}
        />
      </Card>

      <ConfirmModal
        isOpen={!!archiving}
        title="Archive product update"
        message={`Archive '${archiving?.title}'? The announcement disappears from the product drawer and its user preference evaluates Off.`}
        confirmLabel="Archive update"
        testId="feature-flags-updates-archive"
        onClose={() => setArchiving(null)}
        onConfirm={() => {
          if (archiving) archive(archiving);
          setArchiving(null);
        }}
      />
    </>
  );
};

export default UpdatesTab;
